import React, { useState, useRef, useEffect, useMemo } from 'react'
import dayjs from '../../utils/dayjsConfig.js'
import { Box, Typography } from '@mui/material'

import OcDateRangeCard from '../PurchaseOrder/components/DateHeader/OcDateRangeCard.jsx'
import MetricsSummary from './components/CardMetrics/MetricsSummary.jsx'
import Metricstable from '../../components/MetricTable/Metricstable.jsx'
import SectionCard from '../../layout/SectionCard.jsx'
import LoadingScreen from '../../components/LoadingScreen.jsx'
import MetricsHeader from './MetricsHeader.jsx'
import MuiFilterSelect, { DEFAULT_FILTER_VALUE } from '../../components/Filters/MuiFilterSelect.jsx'
import GoogleIcon from '../../icon/GoogleIcon.jsx'

import { useMetrics } from '../PurchaseOrder/hooks/useMetrics.js'
import {
  SUMMARY_REPORTS_CARDS,
  METRICS_TABLE_COLUMNS
} from '../PurchaseOrder/constants/metricConstants.js'
import { buildMetricsSummaryCards } from '../PurchaseOrder/helpers/buildMetricsSummaryCards.js'

import { exportMetricsPDF } from '../PurchaseOrder/helpers/downloads/exportMetricsPDF.js'
import { exportMetricsExcel } from '../PurchaseOrder/helpers/downloads/exportMetricsExcel.js'
import {
  buildFillRateBuyerOptions,
  buildFillRateProductGroupOptions,
  buildFillRateTotals,
  filterFillRateSummary
} from '../PurchaseOrder/helpers/fillRateMetrics.js'
import {
  METRICS_BUYER_FILTER_SX,
  METRICS_FILTER_BAR_SX,
  METRICS_FILTER_TITLE_SX,
  METRICS_PRODUCT_GROUP_FILTER_SX,
  METRICS_RESET_FILTER_TEXT_SX,
  METRICS_RESET_FILTER_WRAPPER_SX
} from './style/metricsIndexStyles.js'

const MetricsIndex = ({ todayOverride }) => {
  const baseDate = todayOverride ? dayjs(todayOverride) : dayjs()
  const today = baseDate.startOf('day')
  const monthStart = baseDate.startOf('month')
  const DEFAULT_DATES = [monthStart, today]
  const title = 'Fill Rate por cadena'

  const [selectedDates, setSelectedDates] = useState(DEFAULT_DATES)
  const [range, setRange] = useState({
    start: monthStart.startOf('day'),
    end: today.endOf('day')
  })
  const [buyerFilter, setBuyerFilter] = useState(DEFAULT_FILTER_VALUE)
  const [productGroupFilter, setProductGroupFilter] = useState(DEFAULT_FILTER_VALUE)

  const { loading, data } = useMetrics({
    startDate: range.start,
    endDate: range.end
  })

  const hasFetchedOnce = useRef(false)

  useEffect(() => {
    if (data && !hasFetchedOnce.current) {
      hasFetchedOnce.current = true
    }
  }, [data])

  const isInitialLoading = loading && !hasFetchedOnce.current

  const summaryCards = buildMetricsSummaryCards(
    SUMMARY_REPORTS_CARDS,
    data
  )
  const summary = data?.summary || []
  const buyerOptions = useMemo(
    () => buildFillRateBuyerOptions(summary),
    [summary]
  )
  const productGroupOptions = useMemo(
    () => buildFillRateProductGroupOptions(summary),
    [summary]
  )
  const filteredSummary = useMemo(
    () =>
      filterFillRateSummary(summary, {
        buyerFilter,
        productGroupFilter
      }),
    [buyerFilter, productGroupFilter, summary]
  )
  const filteredTotals = useMemo(
    () => buildFillRateTotals(filteredSummary),
    [filteredSummary]
  )
  const hasActiveFilters =
    buyerFilter.values.length > 0 ||
    productGroupFilter.values.length > 0

  const dateRangeLabel = `${range.start.format('DD MMM YYYY')} - ${range.end.format('DD MMM YYYY')}`
  const resetFilters = () => {
    setBuyerFilter(DEFAULT_FILTER_VALUE)
    setProductGroupFilter(DEFAULT_FILTER_VALUE)
  }

  if (isInitialLoading) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      <MetricsHeader
        title={title}
        onExportExcel={() =>
          exportMetricsExcel(
            filteredSummary,
            filteredTotals,
            data?.metrics,
            dateRangeLabel
          )
        }
        onExportPDF={() =>
          exportMetricsPDF(
            filteredSummary,
            filteredTotals,
            METRICS_TABLE_COLUMNS,
            data?.metrics,
            dateRangeLabel
          )
        }
      />

      <div className="index-table">
        <div className="show-area">
          <OcDateRangeCard
            value={selectedDates}
            defaultDates={DEFAULT_DATES}
            onChange={dates => {
              setSelectedDates(dates)
              const start = dayjs.min(dates).startOf('day')
              const end = dayjs.max(dates).endOf('day')
              setRange({ start, end })
            }}
            onResetAll={() => {
              setSelectedDates(DEFAULT_DATES)
              setRange({
                start: monthStart.startOf('day'),
                end: today.endOf('day')
              })
            }}
            disabled={loading}
          />

          <Box mt={3}>
            <SectionCard title="RESUMEN GENERAL">
              {loading ? (
                <LoadingScreen text="Cargando..." />
              ) : (
                <MetricsSummary cards={summaryCards} />
              )}
            </SectionCard>
          </Box>

          <Box mt={4}>
            {loading ? (
              <LoadingScreen text="Cargando..." />
            ) : (
              <>
                <Box mb={2}>
                  <Typography variant="subtitle1" sx={METRICS_FILTER_TITLE_SX}>
                    Filtrar información por:
                  </Typography>

                  <Box sx={METRICS_FILTER_BAR_SX}>
                    <GoogleIcon
                      className="filter-icon-poc oc-icon-primary"
                      name="page_info"
                      size={22}
                    />

                    <Box sx={METRICS_BUYER_FILTER_SX}>
                      <MuiFilterSelect
                        title="CADENA"
                        options={buyerOptions}
                        value={buyerFilter}
                        onChange={setBuyerFilter}
                        disabled={buyerOptions.length === 0}
                        disableDropdownFlip
                        searchable
                        width="100%"
                        dropdownWidth={280}
                      />
                    </Box>

                    <Box sx={METRICS_PRODUCT_GROUP_FILTER_SX}>
                      <MuiFilterSelect
                        title="GRUPO DE PRODUCTOS"
                        options={productGroupOptions}
                        value={productGroupFilter}
                        onChange={setProductGroupFilter}
                        disabled={productGroupOptions.length === 0}
                        disableDropdownFlip
                        searchable
                        width="100%"
                        dropdownWidth={320}
                      />
                    </Box>
                  </Box>

                  {hasActiveFilters && (
                    <Box sx={METRICS_RESET_FILTER_WRAPPER_SX}>
                      <Typography
                        variant="body2"
                        className="relation-poc-reset-filter"
                        onClick={resetFilters}
                        sx={METRICS_RESET_FILTER_TEXT_SX}
                      >
                        Restablecer filtros aplicados
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Metricstable
                  summary={filteredSummary}
                  totals={filteredTotals}
                  startDate={range.start}
                  endDate={range.end}
                />
              </>
            )}
          </Box>
        </div>
      </div>
    </>
  )
}

export default MetricsIndex
